import type { HomepageContent, HomepageTheme } from "./homepage";
import { liveV21HomepageBaseline } from "./homepage";

export const cmsTemplateBundleFormat = "wellness-hub-template" as const;
export const cmsTemplateBundleVersion = 1 as const;

export interface CmsBrandAsset {
  readonly id: string;
  readonly kind: "logo" | "icon" | "social-image" | "illustration";
  readonly source: string;
  readonly altText: string;
  readonly width: number;
  readonly height: number;
}

export interface CmsSiteProfile {
  readonly siteId: string;
  readonly organizationId: string;
  readonly templateId: string;
  readonly siteName: string;
  readonly organizationName: string;
  readonly legalName: string;
  readonly primaryDomain: string;
  readonly contactEmail: string;
  readonly contactPhone: string | null;
  readonly locality: string;
  readonly region: string;
  readonly countryCode: string;
  readonly privacyPolicyPath: string;
  readonly termsPath: string | null;
  readonly emergencyNotice: string;
  readonly theme: HomepageTheme;
  readonly assets: readonly CmsBrandAsset[];
  readonly seoDefaults: {
    readonly titleTemplate: string;
    readonly defaultSocialImageAssetId: string | null;
  };
}

export interface CmsNavigationItem {
  readonly id: string;
  readonly label: string;
  readonly href: string;
  readonly placement: "header" | "footer" | "both";
  readonly order: number;
}

export interface CmsRedirect {
  readonly sourcePath: string;
  readonly destinationPath: string;
  readonly status: 301 | 302 | 307 | 308;
}

export interface CmsGlobalBlock {
  readonly id: string;
  readonly kind: "legal-notice" | "contact" | "announcement";
  readonly label: string;
  readonly content: string;
}

export interface CmsTemplateBundle {
  readonly format: typeof cmsTemplateBundleFormat;
  readonly formatVersion: typeof cmsTemplateBundleVersion;
  readonly templateId: string;
  readonly templateVersion: string;
  readonly label: string;
  readonly description: string;
  readonly homepage: HomepageContent;
  readonly profileDefaults: Omit<
    CmsSiteProfile,
    "siteId" | "organizationId" | "primaryDomain" | "assets"
  >;
  readonly navigation: readonly CmsNavigationItem[];
  readonly globalBlocks: readonly CmsGlobalBlock[];
  readonly redirects: readonly CmsRedirect[];
  /** Paths that a cloned site must review before its first publish. */
  readonly requiredContentReviewPaths: readonly string[];
}

export interface CmsInitialUserProposal {
  readonly email: string;
  readonly roleKey: string;
}

export interface CmsInitialResourceProposal {
  readonly name: string;
  readonly kind: "service" | "package" | "room" | "staff";
  readonly description: string;
}

export interface CmsProvisioningDraft {
  readonly templateId: string;
  readonly organizationId: string;
  readonly siteId: string;
  readonly siteName: string;
  readonly organizationName: string;
  readonly legalName: string;
  readonly primaryDomain: string;
  readonly contactEmail: string;
  readonly contactPhone: string | null;
  readonly locality: string;
  readonly region: string;
  readonly countryCode: string;
  readonly privacyPolicyPath: string;
  readonly termsPath: string | null;
  readonly emergencyNotice: string;
  readonly themePreset: HomepageTheme;
  readonly assets: readonly CmsBrandAsset[];
  readonly initialUsers: readonly CmsInitialUserProposal[];
  readonly initialResources: readonly CmsInitialResourceProposal[];
}

export interface CmsProvisioningPlan {
  readonly kind: "cms-provisioning-dry-run";
  readonly planVersion: 1;
  readonly template: { readonly id: string; readonly version: string };
  readonly profile: CmsSiteProfile;
  readonly homepage: HomepageContent;
  readonly navigation: readonly CmsNavigationItem[];
  readonly globalBlocks: readonly CmsGlobalBlock[];
  readonly redirects: readonly CmsRedirect[];
  readonly requiredContentReviewPaths: readonly string[];
  readonly initialUsers: readonly CmsInitialUserProposal[];
  readonly initialResources: readonly CmsInitialResourceProposal[];
  readonly requiresConfirmation: readonly [
    "create-tenant-records",
    "invite-users",
    "configure-domain",
    "provision-external-resources",
    "publish-content",
  ];
}

/** Approved starter template; cloning it never changes this frozen baseline. */
export const whitehavenWellnessStarterTemplate = {
  format: cmsTemplateBundleFormat,
  formatVersion: cmsTemplateBundleVersion,
  templateId: "whitehaven-wellness-v2-1",
  templateVersion: "2.1.0",
  label: "Whitehaven Wellness Hub V2.1",
  description:
    "A responsive wellness-services hub with booking guidance, packages, care boundaries, guides, FAQ, and contact sections.",
  homepage: liveV21HomepageBaseline,
  profileDefaults: {
    templateId: "whitehaven-wellness-v2-1",
    siteName: "Whitehaven Wellness Hub",
    organizationName: "Whitehaven Wellness",
    legalName: "Whitehaven Wellness",
    contactEmail: "info@whitehavenwellness.com",
    contactPhone: null,
    locality: "Hilo",
    region: "HI",
    countryCode: "US",
    privacyPolicyPath: "/privacy",
    termsPath: null,
    emergencyNotice: "Wellness services only · Not emergency or medical care",
    theme: liveV21HomepageBaseline.theme,
    seoDefaults: {
      titleTemplate: "%s — Whitehaven Wellness Hub",
      defaultSocialImageAssetId: null,
    },
  },
  navigation: [
    {
      id: "services",
      label: "Services",
      href: "#services",
      placement: "header",
      order: 10,
    },
    {
      id: "book",
      label: "Book a visit",
      href: "/book",
      placement: "header",
      order: 20,
    },
    {
      id: "privacy",
      label: "Privacy",
      href: "/privacy",
      placement: "footer",
      order: 10,
    },
  ],
  globalBlocks: [
    {
      id: "wellness-boundary",
      kind: "legal-notice",
      label: "Wellness boundary",
      content: "Wellness services only · Not emergency or medical care",
    },
  ],
  redirects: [],
  requiredContentReviewPaths: [
    "careBoundary.body",
    "faq.items.0.answer",
    "faq.items.4.question",
    "faq.items.4.answer",
  ],
} as const satisfies CmsTemplateBundle;
